import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
  getAnn,
} from '@packages/utilities/contextual-expression';
import * as Expressions from './Expressions';

const {everything} = ContextualExpressions;

const initialAnalysis = {
  holes: false,
  errors: false,
  missingSources: false,
} as const;

const resumeAnalysis = Expressions.resume(initialAnalysis);

const stopHole = Expressions.stop({
  holes: true,
  errors: false,
  missingSources: false,
});

const stopMissingSources = Expressions.stop({
  holes: false,
  errors: true,
  missingSources: true,
});

const resumeErrors = Expressions.resume({
  holes: false,
  errors: true,
  missingSources: false,
});

type Analysis = {
  holes: boolean;
  errors: boolean;
  missingSources: boolean;
};

const combineAnalysis = (a1: Analysis) => (a2: Analysis) =>
  a1 === initialAnalysis
    ? a2
    : a2 === initialAnalysis
      ? a1
      : a1.holes === a2.holes &&
          a1.errors === a2.errors &&
          a1.missingSources === a2.missingSources
        ? a1
        : {
            holes: a1.holes || a2.holes,
            errors: a1.errors || a2.errors,
            missingSources: a1.missingSources || a2.missingSources,
          };

const analyze = everything(combineAnalysis)((expr) => {
  if (ContextualExpressions.isHole(stripAnn(expr))) return stopHole;

  const {type, errors} = getAnn(expr);

  // The type-checker tries really hard to associate a type to an expression,
  // even in the presence of type-errors. If it fails to do so it must mean
  // that somewhre inside this expression there was a reference to an
  // undefined value and it cannot come up with a type out of thin air.
  if (!type) return stopMissingSources;

  if (errors.length > 0) return resumeErrors;

  return resumeAnalysis;
});

export const hasMissingSources = (expr: ContextualExpression) =>
  analyze(expr).missingSources;

export const hasErrors = (expr: ContextualExpression) => analyze(expr).errors;

// Checks an annotated expression for Holes
export const hasHoles = (expr: ContextualExpression) => analyze(expr).holes;

// Missing type annotation for a.
// a is a type parameter declared in function type and was implicitly instantiated at call of everything
const anyExpr = Expressions.everything((x) => (y) => x || y);

// Checks a normal expression for Holes
export const hasHolesExpr = anyExpr((theExpr) =>
  Expressions.resume(Expressions.isHole(theExpr))
);

/**
 * For a given flow expression, this function checks
 * that all required fields in the block settings are completed.
 *
 * @param {ContextualExpression} annExpr
 * @returns boolean
 */
export const isExprConfigured = (annExpr: ContextualExpression): boolean => {
  return !hasHoles(annExpr) && !hasErrors(annExpr);
};
